import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import emailjs from 'emailjs-com';
import { FaPaperPlane, FaGithub, FaLinkedin, FaEnvelope } from 'react-icons/fa';

const Section = styled.section`
  margin: 0 auto;
  padding: 60px 20px;
  max-width: 700px;
`;

const SectionLabel = styled.div`
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 0.9rem;
  color: var(--text-muted, #64748b);
  margin-bottom: 30px;

  .prompt {
    color: var(--accent-cyan, #22d3ee);
    margin-right: 8px;
    font-weight: 600;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 36px;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 0.8rem;
  color: var(--text-secondary, #94a3b8);
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(148, 163, 184, 0.1);
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
  backdrop-filter: blur(4px);

  &:hover {
    color: var(--accent-cyan, #22d3ee);
    border-color: rgba(34, 211, 238, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 0.75rem;
  }
`;

const FormTitle = styled.h3`
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 0.85rem;
  color: var(--text-muted, #64748b);
  margin-bottom: 20px;

  span {
    color: var(--accent-green, #10b981);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InputGroup = styled.div`
  position: relative;
`;

const Label = styled.label`
  display: block;
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 0.7rem;
  color: var(--text-muted, #64748b);
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const inputStyles = `
  width: 100%;
  padding: 12px 16px;
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(148, 163, 184, 0.15);
  color: var(--text-primary, #e2e8f0);
  border-radius: 8px;
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 0.85rem;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &:focus {
    border-color: rgba(34, 211, 238, 0.5);
    outline: none;
    box-shadow: 0 0 0 3px rgba(34, 211, 238, 0.08);
  }

  &::placeholder {
    color: var(--text-muted, #64748b);
    opacity: 0.6;
  }
`;

const Input = styled.input`${inputStyles}`;
const Textarea = styled.textarea`
  ${inputStyles}
  resize: vertical;
  min-height: 100px;
`;

const sendAnim = keyframes`
  0% { transform: translateX(0) translateY(0); }
  50% { transform: translateX(4px) translateY(-4px); }
  100% { transform: translateX(0) translateY(0); }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 28px;
  background: rgba(34, 211, 238, 0.1);
  color: var(--accent-cyan, #22d3ee);
  border: 1px solid rgba(34, 211, 238, 0.3);
  cursor: pointer;
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.3s ease;
  border-radius: 8px;
  align-self: flex-start;

  &:hover {
    background: rgba(34, 211, 238, 0.18);
    border-color: rgba(34, 211, 238, 0.5);
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(34, 211, 238, 0.15);

    svg {
      animation: ${sendAnim} 0.6s ease;
    }
  }

  &:active {
    transform: translateY(0);
  }
`;

const Contact = () => {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [status, setStatus] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const sendEmail = (e) => {
    e.preventDefault();
    setStatus('sending');

    emailjs.sendForm('service_79op2sc', 'template_o2440gm', e.target, '2kERr4xVNDry_qKPC')
      .then((result) => {
          setStatus('sent');
          setFormState({ name: '', email: '', message: '' });
          setTimeout(() => setStatus(''), 3000);
      }, (error) => {
          setStatus('error');
          setTimeout(() => setStatus(''), 3000);
      });
  };

  return (
    <Section id="contact">
      <SectionLabel>
        <span className="prompt">$</span> ping dev_kansara
      </SectionLabel>

      <SocialLinks>
        <SocialLink href="https://www.linkedin.com/in/devkansara/" target="_blank" rel="noopener noreferrer">
          <FaLinkedin size={16} /> LinkedIn
        </SocialLink>
        <SocialLink href="https://github.com/devkansara/" target="_blank" rel="noopener noreferrer">
          <FaGithub size={16} /> GitHub
        </SocialLink>
        <SocialLink href="mailto:devkansa@usc.edu">
          <FaEnvelope size={14} /> Email
        </SocialLink>
      </SocialLinks>

      <FormTitle>{/* Or send me a message directly */}Or drop me a message</FormTitle>
      <Form onSubmit={sendEmail}>
        <InputGroup>
          <Label htmlFor="contact-name">Name</Label>
          <Input
            id="contact-name"
            type="text"
            name="name"
            placeholder="Your name"
            value={formState.name}
            onChange={handleChange}
            required
          />
        </InputGroup>
        <InputGroup>
          <Label htmlFor="contact-email">Email</Label>
          <Input
            id="contact-email"
            type="email"
            name="email"
            placeholder="your@email.com"
            value={formState.email}
            onChange={handleChange}
            required
          />
        </InputGroup>
        <InputGroup>
          <Label htmlFor="contact-message">Message</Label>
          <Textarea
            id="contact-message"
            name="message"
            placeholder="What's on your mind?"
            value={formState.message}
            onChange={handleChange}
            rows="4"
            required
          />
        </InputGroup>
        <Button type="submit" disabled={status === 'sending'}>
          {status === 'sending' ? 'Sending...' : status === 'sent' ? '✓ Sent!' : status === 'error' ? 'Failed, try again' : <><span>Send Message</span> <FaPaperPlane size={14} /></>}
        </Button>
      </Form>
    </Section>
  );
};

export default Contact;